import React, { useEffect, useState } from 'react';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';
import { tooltips } from '../utils/ABHelper';

type InputBoxWinThresholdProps = {
    initialValue?: number;
    onChange: (value: number) => void;
    label: string;
    'data-testid'?: string;
}

/**
 * Minimum Lift Percentage (`okf/design-system/e2e.md` § 144).
 *
 * **Every line of the validation below is unchanged** — `validateNumber`'s regex,
 * `normalizeNumber`'s leading-zero strip, the "only emit on a non-empty value" rule and
 * the blur-to-zero fallback. This value goes to the backend as `win_threshold` and
 * decides whether a variant is declared a winner, so it is the last thing a restyle
 * should touch.
 *
 * What changed is the chrome: a bare `<input>` with `shadow-none drop-down-dm` and a
 * `<label>` with no `htmlFor` becomes a `TextField`, and the `%` suffix becomes a
 * typographic token instead of a raw text node inheriting `body { font-size: .9rem }`.
 *
 * `type="text"`, deliberately, exactly as before: the field accepts a decimal point and
 * `type="number"` would hand the browser's own spinner and locale parsing a value this
 * component already validates itself.
 */
function InputBoxWinThreshold({ initialValue = 0, onChange, label, 'data-testid': dataTestId }: InputBoxWinThresholdProps) {
    const [value, setValue] = useState<string>(initialValue.toString());

    useEffect(() => {
        setValue(initialValue.toString());
    }, [initialValue]);

    const normalizeNumber = (value: string): string => {
        if (value === '') return value

        const parts = value.split('.')
        const integerPart = parts[0].replace(/^0+/, '') || '0'
        const decimalPart = parts[1] || ''

        const normalized = [
            integerPart,
            decimalPart ? '.' + decimalPart : ''
        ].join('')

        return normalized
    }

    const validateNumber = (value: string): boolean => {
        // Only allow positive numbers or empty string
        const numberRegex = /^\d*(?:\.\d*)?$/
        if (!numberRegex.test(value)) return false
        if (value === '') return true

        // Ensure the number is not negative
        const num = parseFloat(value)
        return !isNaN(num) && num >= 0
    }

    const handleChange = (newValue: string) => {
        if (!validateNumber(newValue)) return

        const normalizedValue = normalizeNumber(newValue)
        setValue(normalizedValue)

        if (normalizedValue !== '') {
            onChange(parseFloat(normalizedValue))
        }
    }

    const handleBlur = () => {
        if (value === '') {
            setValue('0')
            onChange(0)
        }
    }

    return (
        <div>
            <div className="tw-flex tw-items-center tw-gap-2">
                <Typography variant="title" as="span" className="tw-text-ink-tertiary">
                    {label}
                </Typography>
                {/*
                  `InfoTooltip` rather than the Rules `OverlayIcon`: the legacy glyph was
                  a hover-only SVG with no `role`, no accessible name and no keyboard
                  path, so the note explaining what a whole-number MLP means was
                  pointer-users-only.
                */}
                <InfoTooltip
                    placement="right"
                    label={`About ${label}`}
                    data-testid={dataTestId ? `${dataTestId}-info` : undefined}
                    message={
                        <>
                            <p>{tooltips["Minimum Lift Percentage"]}</p>
                            <br />
                            <p>{tooltips["Minimum Lift Percentage Note"]}</p>
                        </>
                    }
                />
            </div>
            <div className="tw-flex tw-items-center tw-gap-3 tw-mt-2">
                <TextField
                    label=""
                    aria-label={label}
                    type="text"
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="tw-w-32"
                    data-testid="components-inputboxwinthreshold-input-1"
                />
                <Typography variant="body-primary" as="span" className="tw-text-ink">%</Typography>
            </div>
        </div>
    );
}

export default InputBoxWinThreshold
